import React, { useEffect } from 'react';
import { Box, Typography, IconButton, Paper, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Animated notification popup component
 * @param {Object} props - Component props
 * @param {string} props.type - Notification type: 'success', 'error', 'info', 'warning'
 * @param {string} props.message - Message to display
 * @param {boolean} props.open - Whether notification is visible
 * @param {Function} props.onClose - Close callback function
 * @param {number} props.autoHideDuration - Auto-hide duration in ms (default: 5000)
 * @returns {JSX.Element} - Notification component
 */
const NotificationPopup = ({
  type = 'info',
  message = '',
  open = false,
  onClose,
  autoHideDuration = 5000,
}) => {
  const theme = useTheme();
  
  // Close notification after auto-hide duration
  useEffect(() => {
    if (open && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);
      
      return () => clearTimeout(timer);
    }
  }, [open, onClose, autoHideDuration]);

  // Define colors based on notification type
  const getTypeColor = () => {
    switch (type) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
      default:
        return theme.palette.info.main;
    }
  };
  
  // Get icon based on notification type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  };
  
  // Animation variants
  const variants = {
    initial: {
      opacity: 0,
      y: 50,
      scale: 0.8,
      transformOrigin: 'bottom right',
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: {
        duration: 0.3,
      },
    },
  };
  
  // Progress bar animation
  const progressVariants = {
    initial: {
      width: '100%',
    },
    animate: {
      width: '0%',
      transition: {
        duration: autoHideDuration / 1000,
        ease: 'linear',
      },
    },
  };

  return (
    <AnimatePresence>
      {open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 2000,
          }}
        >
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
          >
            <Paper
              elevation={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: 320,
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                borderLeft: `6px solid ${getTypeColor()}`,
              }}
            >
              <Box
                sx={{
                  padding: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    color: getTypeColor(),
                    display: 'flex',
                    mr: 1.5,
                    mt: 0.25,
                  }}
                >
                  {getIcon()}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    sx={{ mb: 0.5, textTransform: 'capitalize' }}
                  >
                    {type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {message}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <motion.div
                variants={progressVariants}
                initial="initial"
                animate="animate"
                style={{
                  height: 2,
                  background: getTypeColor(),
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                }}
              />
            </Paper>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default NotificationPopup; 